/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import type { TrackingData } from 'js/lib/retracked';
import { useRetracked } from 'js/lib/retracked';
import useRouter from 'js/lib/useRouter';

import { Link, Typography2, useTheme } from '@coursera/cds-core';
import type { LinkProps } from '@coursera/cds-core';
import { ArrowNextIcon } from '@coursera/cds-icons';

import withSingleTracked from 'bundles/common/components/withSingleTracked';
import TrackedDiv from 'bundles/page/components/TrackedDiv';
import { useTracker, useVisibilityTracker } from 'bundles/page/lib/event-pulse/react';

import _t from 'i18n!nls/program-home';

import SkillSetIcon from './SkillSetIcon';

const TrackedLink = withSingleTracked({ type: 'BUTTON' })<LinkProps>(Link);

const useStyles = () => {
  const { spacing, breakpoints, palette } = useTheme();
  return {
    skillsetCard: css`
      border: 1px solid ${palette.gray[500]};
      background: ${palette.white};
      padding: ${spacing(24)};
      cursor: pointer;
      transition: box-shadow 0.4s;

      &:hover {
        box-shadow: 0 4px 4px rgb(0 0 0 / 25%);
      }
    `,
    links: css`
      display: flex;
      flex-wrap: wrap;
      gap: ${spacing(24)};
      margin-top: ${spacing(8)};
    `,
    link: css`
      display: flex;
      align-items: center;
      gap: ${spacing(4)};
      ${breakpoints.down('sm')} {
        width: 100%;
      }
    `,
    header: css`
      text-transform: uppercase;
      display: flex;
      align-items: center;
      gap: ${spacing(8)};
      padding-bottom: ${spacing(8)};
    `,
  };
};

type Props = {
  className?: string;
  skillsetName: string;
  url: string;
  trackingData: TrackingData;
  source: string;
};

const NewSkillSetCard: React.FunctionComponent<Props> = (props) => {
  const styles = useStyles();
  const router = useRouter();
  const track = useRetracked();
  const newTracker = useTracker();
  const trackingName = 'search_skillset_card';
  const { trackingData } = props;
  const { skillset } = trackingData;

  const newEventingProductData = {
    productCard: {
      index: trackingData.index ?? 0,
    },
    product: {
      id: skillset.objectId,
      name: skillset.name,
    },
  };

  const skillSetCardRef: React.MutableRefObject<HTMLDivElement | null> = useVisibilityTracker(
    'view_product_card',
    newEventingProductData
  );

  const handleClick = () => {
    newTracker('click_product_card', newEventingProductData);

    track({
      trackingName,
      trackingData: props.trackingData,
      action: 'click',
    });
    router.push(`${props.url}?source=${props.source}`);
  };

  return (
    // This is not the only way to get to the skills recs, there is a link which will be more useful for a11y and keyboard
    <div
      ref={skillSetCardRef}
      role="presentation"
      className={props.className}
      css={styles.skillsetCard}
      onClick={handleClick}
    >
      <TrackedDiv withVisibilityTracking trackingName={trackingName} data={props.trackingData}>
        <Typography2 variant="subtitleMedium" component="h2" css={styles.header}>
          <SkillSetIcon />
          {_t('SkillSet')}
        </Typography2>
        <Typography2 variant="subtitleMedium" component="h2">
          {props.skillsetName}
        </Typography2>
        <div css={styles.links}>
          <TrackedLink
            {...Link.defaultProps}
            css={styles.link}
            tabIndex={0}
            trackingName="search_banner_skillset_card"
            trackingData={props.trackingData}
            href={`${props.url}?source=${props.source}`}
            variant="quiet"
            onClick={(e) => e.stopPropagation()}
          >
            <Typography2
              color="inherit"
              variant="subtitleMedium"
              component="span"
              aria-label={_t('View recommendations for #{skillsetName}', { skillsetName: props.skillsetName })}
            >
              {_t('View recommendations')}
            </Typography2>
            <ArrowNextIcon />
          </TrackedLink>
        </div>
      </TrackedDiv>
    </div>
  );
};

export default NewSkillSetCard;
